import t from "@/lang/lang";
import { BranchFilter } from "@/services/backend/company/components/branch-filter";
import { CompanyFilter } from "@/services/backend/company/components/company-filter";
import { CustomerFilter } from "@/services/backend/customers/components/customer-filter";
import { ServiceObjectFilter } from "@/services/backend/customers/components/service-object-filter";
import { DateBetweenFilter } from "@/shared/components/filter/date-between-filter";
import { OrderBySelect } from "@/shared/components/tables/order-by-select";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableCard } from "@/shared/components/tables/table-card";
import { CompanyName } from "@/services/backend/company/components/company-name";
import { BranchName } from "@/services/backend/company/components/branch-name";
import { formatDateTime } from "@/shared/lib/utilities/date";
import { Edit } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { CustomerName } from "@/services/backend/customers/components/customer-name";
import { Input } from "@/shared/components/ui/input";
import { FilterMode } from "@/shared/lib/filter/filter";
import { Spinner } from "@/shared/components/ui/spinner";
import { OutboxGesecInvoice } from "../types/outbox-gesec-invoice";
import {
  OutboxGesecInvoiceListRequest,
  OutboxGesecInvoiceOrderByFields,
  useBusinessCentralOutboxGesecInvoicesListQuery,
} from "@/services/backend/business-central/api/outbox-gesec-invoice-api";
import { ReactNode } from "react";
import { OutboxStatusIcon } from "./outbox-status-icon";

export function OutboxGesecInvoiceTable({
  request,
  onRequestChange,
  renderRowLink,
}: {
  request: OutboxGesecInvoiceListRequest;
  onRequestChange: (request: OutboxGesecInvoiceListRequest) => void;
  renderRowLink: (outboxId: string, children: ReactNode) => ReactNode;
}) {
  const { data, isLoading, isFetching, error } =
    useBusinessCentralOutboxGesecInvoicesListQuery(request);

  const colCount = 7;

  return (
    <div className="grid gap-2">
      <TableCard>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Status")}</TableHead>
              <TableHead>{t("Unternehmen")}</TableHead>
              <TableHead>{t("Kunde")}</TableHead>
              <TableHead>{t("Niederlassung")}</TableHead>
              <TableHead>{t("Dokumente")}</TableHead>
              <TableHead>{t("Erstellt am")}</TableHead>
              <TableHead>{t("Geändert am")}</TableHead>
              <TableHead>{t("Aktionen")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={colCount} />}
            {error && <TableRtkErrorRow colSpan={colCount} error={error} />}
            {data && data.items.length === 0 && (
              <TableEmptyRow colSpan={colCount} />
            )}
            {data &&
              data.items.map((invoice) => (
                <InvoiceRow
                  key={invoice.outboxId}
                  invoice={invoice}
                  renderRowLink={renderRowLink}
                />
              ))}
          </TableBody>
        </Table>
      </TableCard>
      <div className="flex items-center">
        {isFetching && <Spinner className="ml-4" />}
        {data && (
          <PaginationLimitOffset
            offset={request.offset ?? 0}
            limit={request.limit ?? 0}
            totalCount={data.totalCount}
            setLimitOffset={(limit, offset) => {
              onRequestChange({ ...request, limit, offset });
            }}
            className="ml-auto"
          />
        )}
      </div>
    </div>
  );
}

function InvoiceRow({
  invoice,
  renderRowLink,
}: {
  invoice: OutboxGesecInvoice;
  renderRowLink: (outboxId: string, children: ReactNode) => ReactNode;
}) {
  return (
    <TableRow>
      <TableCell className="pl-4">
        <OutboxStatusIcon status={invoice.outboxStatus} />
      </TableCell>
      <TableCell>
        <CompanyName companyId={invoice.outboxCompanyId} short />
      </TableCell>
      <TableCell>
        <CustomerName customerId={invoice.outboxCustomerId} />
      </TableCell>
      <TableCell>
        {invoice.outboxBranchIds?.map((branchId) => (
          <BranchName key={branchId} branchId={branchId} />
        ))}
      </TableCell>
      <TableCell>
        {invoice.documents
          .map((reference) => reference.outboxDocumentNumber)
          .join(", ")}
      </TableCell>
      <TableCell>{formatDateTime(invoice.outboxCreatedAt)}</TableCell>
      <TableCell>{formatDateTime(invoice.outboxUpdatedAt)}</TableCell>
      <TableCell className="text-right">
        {renderRowLink(
          invoice.outboxId,
          <Button variant="outline" size="sm">
            <Edit />
          </Button>,
        )}
      </TableCell>
    </TableRow>
  );
}

export function OutboxGesecInvoiceTableFilters({
  request,
  onRequestChange,
  hideCompanyFilter,
  hideBranchFilter,
  hideCustomerFilter,
  hideServiceObjectFilter,
}: {
  request: OutboxGesecInvoiceListRequest;
  onRequestChange: (request: OutboxGesecInvoiceListRequest) => void;
  hideCompanyFilter?: boolean;
  hideBranchFilter?: boolean;
  hideCustomerFilter?: boolean;
  hideServiceObjectFilter?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <Input
        value={request.outboxDocumentNumber?.values?.[0] ?? ""}
        onChange={(e) => {
          const { value } = e.target;
          onRequestChange({
            ...request,
            outboxDocumentNumber: {
              active: value.length > 0,
              mode: FilterMode.Like,
              values: value.length > 0 ? [value] : [],
            },
          });
        }}
        className="h-8"
        placeholder={t("Dokumentnummer")}
      />
      <CompanyFilter
        filter={request.outboxCompany}
        onFilterChange={(outboxCompany) =>
          onRequestChange({ ...request, outboxCompany })
        }
        hidden={hideCompanyFilter}
      />
      <BranchFilter
        filter={request.outboxBranch}
        onFilterChange={(outboxBranch) =>
          onRequestChange({ ...request, outboxBranch })
        }
        companyIds={
          request.outboxCompany?.active
            ? request.outboxCompany.values
            : undefined
        }
        hidden={hideBranchFilter}
      />
      <CustomerFilter
        filter={request.outboxCustomer}
        onFilterChange={(outboxCustomer) =>
          onRequestChange({ ...request, outboxCustomer })
        }
        hidden={hideCustomerFilter}
      />
      <ServiceObjectFilter
        filter={request.outboxServiceObject}
        onFilterChange={(outboxServiceObject) =>
          onRequestChange({ ...request, outboxServiceObject })
        }
        customerIds={
          request.outboxCustomer?.active
            ? request.outboxCustomer.values
            : undefined
        }
        hidden={hideServiceObjectFilter}
      />
      <DateBetweenFilter
        label={t("Erstellt am")}
        filter={request.outboxCreatedAt}
        onFilterChange={(outboxCreatedAt) =>
          onRequestChange({ ...request, outboxCreatedAt })
        }
      />
      <DateBetweenFilter
        label={t("Geändert am")}
        filter={request.outboxUpdatedAt}
        onFilterChange={(outboxUpdatedAt) =>
          onRequestChange({ ...request, outboxUpdatedAt })
        }
      />
      <OrderBySelect<OutboxGesecInvoiceOrderByFields>
        orderBy={request.orderBy}
        onOrderByChange={(orderBy) => onRequestChange({ ...request, orderBy })}
        fields={[
          { label: t("Status"), value: "outboxStatus" },
          { label: t("Erstellt am"), value: "outboxCreatedAt" },
          { label: t("Geändert am"), value: "outboxUpdatedAt" },
        ]}
      />
    </div>
  );
}
